var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Paginator_1;
import React, { Children, Component, Fragment } from 'react';
import { ButtonTheme } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, eventListener, expression, number, object, oneOf } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { PageEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { classNames, enumValues, getLostProps, isFunction, isMobile, propagateData } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as ChevronIcon } from '../../../resources/assets/components/DLS_Chevron-Left-Dark.svg';
import { ReactComponent as ArrowIcon } from '../../../resources/assets/components/DLS_Navigation-Left-Dark.svg';
import { Button } from '../button/button.js';
import { FetchContext } from '../fetch/fetch-context.js';
import PaginatorAPI from './paginator-api.js';
import { PAGINATOR_MAX_NUMBER_OF_PAGES_RENDERED, PAGINATOR_TRUNCATE_CHARACTER, PAGINATOR_TRUNCATE_PAGE_BREAKPOINT, PaginatorControlType, PaginatorStartPage } from './paginator-constants.js';
import { getNumberOfPages } from './paginator-utils.js';
import './paginator.css';
const PaginatorContainer = {
    acceptedTypes: [
        ComponentType.LIST,
    ],
    name: '',
};
let Paginator = Paginator_1 = class Paginator extends Component {
    constructor() {
        super(...arguments);
        this.onNextPage = (pageNumber, accumulate = false) => {
            const { onNextPage, limit } = this.props;
            if (isFunction(onNextPage)) {
                onNextPage(new PageEvent({ accumulate, limit, mergeParams: false, page: pageNumber }));
            }
        };
        this.onPrevPage = (pageNumber) => {
            const { onPrevPage, limit } = this.props;
            if (isFunction(onPrevPage)) {
                onPrevPage(new PageEvent({ limit, mergeParams: false, page: pageNumber }));
            }
        };
        this.onPageControlClick = (pageNumberProp) => {
            const { page, startPage } = this.props;
            const pageNumber = Number(startPage) === 0 ? pageNumberProp - 1 : pageNumberProp;
            if (pageNumber === page) {
                return;
            }
            if (pageNumber > page) {
                return this.onNextPage(pageNumber);
            }
            this.onPrevPage(pageNumber);
        };
    }
    componentDidMount() {
        const { lazy, limit, page, startPage } = this.props;
        if (lazy) {
            this.context?.setInitialPagination({ accumulate: false, limit, page: 0 });
            return;
        }
        this.onNextPage(page ?? Number(startPage));
    }
    componentDidUpdate(prevProps) {
        const { startPage } = this.props;
        if (prevProps.startPage !== startPage) {
            this.onNextPage(Number(startPage));
        }
    }
    getPageControlClassName(pageNumber) {
        const { page: pageProps, startPage } = this.props;
        const page = Number(startPage) === 0 ? pageProps + 1 : pageProps;
        return classNames({
            'c-paginator__control': true,
            'c-paginator__control--is-active': pageNumber === page,
            'c-paginator__control-page': true,
        });
    }
    getArrowOrChevronControlClassName(mainClass, diffNumber, isRightSide = false) {
        const { limit, page, totalCount, startPage } = this.props;
        const numberOfPages = getNumberOfPages(totalCount, limit);
        const sumResult = page + diffNumber;
        const minLimit = Number(startPage) === 0 ? -1 : 0;
        const maxLimit = Number(startPage) === 0 ? numberOfPages - 1 : numberOfPages;
        const isDisabled = isRightSide ? sumResult > maxLimit : sumResult === minLimit;
        return classNames({
            'c-paginator__control': true,
            [`c-paginator__control-${mainClass}`]: true,
            'c-paginator__control--is-disabled': isDisabled,
            'c-paginator__control--is-right': isRightSide,
        });
    }
    getPageText(pagesArray, index) {
        const { page } = this.props;
        const isSecondPageAndNeedTruncation = index === 1 && page - pagesArray[0] > PAGINATOR_TRUNCATE_PAGE_BREAKPOINT;
        const isFourthPageAndNeedTruncation = index === 3 && pagesArray[pagesArray.length - 1] - page > PAGINATOR_TRUNCATE_PAGE_BREAKPOINT;
        return (isSecondPageAndNeedTruncation || isFourthPageAndNeedTruncation)
            ? PAGINATOR_TRUNCATE_CHARACTER
            : String(pagesArray[index]);
    }
    getActivePageSiblings(pagesArray) {
        const { page: pageProps, startPage } = this.props;
        const page = Number(startPage) === 0 ? pageProps + 1 : pageProps;
        return pagesArray.filter((item, index) => {
            const isFirstPage = index === 0;
            const isLastPage = index === pagesArray.length - 1;
            if (isFirstPage || isLastPage) {
                return true;
            }
            const isActivePage = item === page;
            const isNextPage = item === (page + 1);
            const isSecondNextPage = item === (page + 2);
            const isThirdNextPage = item === (page + 3);
            const isThirdPreviousPage = item === (page - 3);
            const isSecondPreviousPage = item === (page - 2);
            const isPreviousPage = item === (page - 1);
            const pageHasEvent = isPreviousPage || isActivePage || isNextPage;
            const siblingPagesHash = {
                [pagesArray[0]]: isActivePage || isNextPage || isSecondNextPage || isThirdNextPage,
                [pagesArray[1]]: isActivePage || isNextPage || isSecondNextPage,
                [pagesArray[pagesArray.length - 1]]: isThirdPreviousPage || isSecondPreviousPage || isPreviousPage || isActivePage,
                [pagesArray[pagesArray.length - 2]]: isSecondPreviousPage || isPreviousPage || isActivePage,
            };
            const siblingPageHash = siblingPagesHash[page];
            return siblingPageHash ?? pageHasEvent;
        });
    }
    renderPageControls() {
        const { limit, totalCount } = this.props;
        const numberOfPages = getNumberOfPages(totalCount, limit);
        const numberOfPagesAsArray = Array.from(Array(numberOfPages + 1).keys()).slice(1);
        if (!numberOfPages) {
            return null;
        }
        const limitToFivePages = numberOfPages > PAGINATOR_MAX_NUMBER_OF_PAGES_RENDERED;
        const finalPagesArray = limitToFivePages
            ? this.getActivePageSiblings(numberOfPagesAsArray)
            : numberOfPagesAsArray;
        const pagesControls = finalPagesArray.map((item, idx) => React.createElement("div", { className: this.getPageControlClassName(item), key: item, onClick: () => this.onPageControlClick(item) }, limitToFivePages
            ? this.getPageText(finalPagesArray, idx)
            : item));
        return pagesControls;
    }
    renderArrowOrChevronControl(diffNumber, controlType, pageNumber) {
        const diffNumberIsPositive = diffNumber > 0;
        const onClickAction = diffNumberIsPositive ? this.onNextPage : this.onPrevPage;
        return (React.createElement("div", { className: this.getArrowOrChevronControlClassName(controlType, diffNumber, diffNumberIsPositive), onClick: () => onClickAction(pageNumber) }, controlType === PaginatorControlType.ARROW
            ? React.createElement(ArrowIcon, null)
            : React.createElement(ChevronIcon, null)));
    }
    renderControls() {
        const { limit, loadMoreExpression, page, totalCount, startPage } = this.props;
        const numberOfPages = getNumberOfPages(totalCount, limit);
        const maxNumberOfPages = Number(startPage) === 0 ? numberOfPages - 1 : numberOfPages;
        if (isMobile()) {
            if (page === maxNumberOfPages || maxNumberOfPages === 0) {
                return null;
            }
            return (React.createElement(Button, { className: "c-paginator__load-more", onClick: () => this.onNextPage(page + 1, true), theme: ButtonTheme.SECONDARY, value: loadMoreExpression }));
        }
        return (React.createElement(Fragment, null,
            this.renderArrowOrChevronControl(-1, PaginatorControlType.CHEVRON, Number(startPage) === 0 ? 0 : 1),
            this.renderArrowOrChevronControl(-1, PaginatorControlType.ARROW, page - 1),
            this.renderPageControls(),
            this.renderArrowOrChevronControl(1, PaginatorControlType.ARROW, page + 1),
            this.renderArrowOrChevronControl(1, PaginatorControlType.CHEVRON, maxNumberOfPages)));
    }
    renderContent() {
        const { data, children, ...otherProps } = this.props;
        if (!children || !Children.toArray(children).length) {
            return null;
        }
        const lostProps = getLostProps(Paginator_1.propTypes, otherProps);
        return (React.createElement("div", { className: "c-paginator__content" }, propagateData(children, data, lostProps)));
    }
    render() {
        const { uuid } = this.props;
        return (React.createElement(Container, { acceptedTypes: PaginatorContainer.acceptedTypes, className: "c-paginator", uuid: uuid },
            this.renderContent(),
            React.createElement("div", { className: "c-paginator__controls" }, this.renderControls())));
    }
};
Paginator.contextType = FetchContext;
Paginator.propTypes = {
    data: arrayOf(object).notEnumerable(),
    lazy: bool,
    limit: number,
    loadMoreExpression: expression,
    onNextPage: eventListener,
    onPrevPage: eventListener,
    startPage: oneOf(enumValues(PaginatorStartPage)),
};
Paginator.defaultProps = {
    limit: 10,
    loadMoreExpression: 'tws.paginator.load-more',
    startPage: '1',
    totalCount: 0,
};
Paginator = Paginator_1 = __decorate([
    AwesomeComponent({ api: PaginatorAPI, containers: [PaginatorContainer], type: ComponentType.BEHAVIOR })
], Paginator);
export { Paginator, };
export default Paginator;
//# sourceMappingURL=paginator.js.map